/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { InlineNotification } from 'carbon-components-react';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Instance } from '../../../../ts/instance';
import { selectInaccessibleAccountNames } from '../../store/instances/selectors';
import { getBranding } from '../../util/branding';
import InstancesList from './InstancesList';
import InstancesLoading from './InstancesLoading';
import InstancesUpdating from './InstancesUpdating';
import messages from './messages';
import NoInstances from './NoInstancesContainer';

import './Instances.scss';

export interface InstancesProps {
  instances: Instance[];
  loading: boolean;
  updating?: boolean;
}

const Instances: React.FC<InstancesProps> = ({
  instances,
  loading,
  updating
}) => {
  const intl = useIntl();
  const { productName } = getBranding();

  const inaccessibleAccountNames = useSelector(selectInaccessibleAccountNames);

  const content = [];

  if (loading) {
    content.push(<InstancesLoading key="0" />);
  } else {
    if (!updating && (!instances || instances.length === 0)) {
      content.push(<NoInstances key="1" />);
    } else {
      if (
        Array.isArray(inaccessibleAccountNames) &&
        inaccessibleAccountNames.length
      ) {
        const accountNamesString = inaccessibleAccountNames.join(', ');

        content.push(
          <InlineNotification
            key="2"
            kind="info"
            hideCloseButton
            title={intl.formatMessage(messages.inaccessibleAccountsTitle)}
            subtitle={accountNamesString}
          />
        );
      }

      content.push(
        <div className="bx--row" key="3">
          <div className="bx--col">
            <FormattedMessage
              tagName="h2"
              {...messages.selectInstance}
              values={{ productName }}
            />
          </div>
        </div>
      );
      if (updating) {
        content.push(
          <div className="bx--row" key="4">
            <div className="bx--col">
              <InstancesUpdating />
            </div>
          </div>
        );
      }
      content.push(<InstancesList key="5" instances={instances} />);
    }
  }

  return <div className="bx--grid--full-width Instances">{content}</div>;
};

export default Instances;
